import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Sparkles, Send, Bot, User as UserIcon, Loader2, Rocket, Timer, Route,
    Ship, Anchor, TrendingDown, DollarSign, CheckCircle2, RefreshCw, Receipt,
    ChevronRight, X
} from 'lucide-react';
import { toast } from 'sonner';
import { baseURL } from '@/config/APIConfig';
import CarrierLogo from '@/components/CarrierLogo';

const API_URL = baseURL.AiBaseURL;
const RATE_API_URL = baseURL.AiBaseURL; // production rate-matrix API

// Map RIA container labels → Rate Matrix API container names
const CONTAINER_API_MAP = {
    '20GP': { apiName: '20 STD', value: '20STD' },
    '40GP': { apiName: '40 STD', value: '40STD' },
    '40HC': { apiName: '40 HC', value: '40HC' },
    '40HQ': { apiName: '40 HC', value: '40HC' },
    '45HC': { apiName: '45 DC', value: '45HC' },
    'Reefer 20': { apiName: '20 REF', value: '20RF' },
    'Reefer 40': { apiName: '40 REF', value: '40RF' },
};

// Demo rate data (used when no user override)
const DEFAULT_LANE = {
    origin: 'New York',
    originCode: 'USNYC',
    destination: 'Liverpool',
    destinationCode: 'GBLIV',
    container: '40HQ',
    commodity: 'Newsprint',
    doorZip: '',
};

const CONTAINER_TYPES = ['20GP', '40GP', '40HC', '40HQ', '45HC', 'Reefer 20', 'Reefer 40'];

// Move-type options shown before the lane setup card
const MOVE_TYPES = [
    { id: 'door-to-port', label: 'Door to Port', description: 'Pickup from shipper door, deliver to destination port' },
    { id: 'port-to-door', label: 'Port to Door', description: 'Origin port to USA delivery address (door)' },
    { id: 'port-to-port', label: 'Port to Port', description: 'Origin port to destination port (CY/CY)' },
];

// Currency formatter — always show exactly 2 decimal places (e.g. $760.57, $2,232.00)
const fmtUSD = (v) =>
    Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DEMO_RATES = [
    { type: 'Contract', carrier: 'Contract', rate: 750, transitDays: 14, reliability: 98 },
    { type: 'Spot', carrier: 'CMA CGM', rate: 610, transitDays: 11, reliability: 89 },
    { type: 'ShipEz', carrier: 'MSC', rate: 680, transitDays: 13, reliability: 95 },
];

// Question flow — exactly 3 questions, multiple choice where applicable
const QUESTIONS = [
    {
        id: 'urgency',
        prompt: "First — what's your shipment urgency?",
        options: ['Low', 'Medium', 'High'],
    },
    {
        id: 'priority',
        prompt: "Got it. What matters most to you?",
        options: ['Lowest Cost', 'Fastest Transit', 'Balanced'],
    },
    {
        id: 'carrier',
        prompt: "Last one — any preferred carrier? (Type a carrier name or tap 'No preference')",
        options: ['No preference', 'MSC', 'CMA CGM', 'Maersk'],
        allowFreeText: true,
    },
];

// Ranking logic — returns rates with `tags[]` + `savings` + `recommended: true`
const rankRates = (rates, answers) => {
    const cheapest = [...rates].sort((a, b) => a.rate - b.rate)[0];
    const fastest = [...rates].sort((a, b) => a.transitDays - b.transitDays)[0];
    const contract = rates.find(r => r.type === 'Contract');

    let recommended = cheapest;
    const priority = (answers.priority || '').toLowerCase();
    const urgency = (answers.urgency || '').toLowerCase();
    const carrierPref = (answers.carrier || '').toLowerCase();

    if (priority.includes('fastest')) {
        recommended = fastest;
    } else if (priority.includes('lowest') || priority.includes('cost')) {
        recommended = cheapest;
    } else {
        // Balanced — score by (rate/maxRate) + (transit/maxTransit)
        const maxRate = Math.max(...rates.map(r => r.rate));
        const maxTransit = Math.max(...rates.map(r => r.transitDays));
        recommended = [...rates].sort((a, b) => {
            const sa = a.rate / maxRate + a.transitDays / maxTransit;
            const sb = b.rate / maxRate + b.transitDays / maxTransit;
            return sa - sb;
        })[0];
    }

    // Urgency override — High urgency trumps "lowest cost" in favor of fastest if transit delta > 2 days
    if (urgency === 'high' && recommended.transitDays > fastest.transitDays + 2) {
        recommended = fastest;
    }
    // Low urgency — prefer cheapest even in Balanced mode
    if (urgency === 'low' && priority.includes('balanced')) {
        recommended = cheapest;
    }

    // Preferred carrier override — if user's carrier choice matches, promote it
    if (carrierPref && !['', 'no preference'].includes(carrierPref)) {
        const match = rates.find(r => r.carrier.toLowerCase().includes(carrierPref));
        if (match) recommended = match;
    }

    return rates.map(r => ({
        ...r,
        tags: [
            r.rate === cheapest.rate ? 'Best Price' : null,
            r.transitDays === fastest.transitDays ? 'Fastest' : null,
            r === recommended ? 'Recommended' : null,
        ].filter(Boolean),
        savings: contract && r.rate < contract.rate ? contract.rate - r.rate : 0,
        recommended: r === recommended,
    }));
};

// Generate plain-English recommendation based on answers + winner
const buildInsight = (ranked, answers, lane) => {
    const winner = ranked.find(r => r.recommended);
    if (!winner) return '';
    const urgency = (answers.urgency || '').toLowerCase();
    const priority = (answers.priority || '').toLowerCase();
    const carrierPref = (answers.carrier || '').trim();
    const cheapest = [...ranked].sort((a, b) => a.rate - b.rate)[0];
    const fastest = [...ranked].sort((a, b) => a.transitDays - b.transitDays)[0];

    let reason = '';
    if (priority.includes('lowest')) {
        reason = `you flagged cost as the top priority, and ${winner.carrier}'s ${winner.type} rate at $${winner.rate}/container is the cheapest option on this lane`;
    } else if (priority.includes('fastest')) {
        reason = `you need it fast — ${winner.carrier} delivers in ${winner.transitDays} days, which is the shortest transit available`;
    } else {
        reason = `you're optimizing both cost and speed — ${winner.carrier}'s ${winner.type} at $${winner.rate}/container keeps the total cost low without adding too many days to transit`;
    }

    let urgencyNote = '';
    if (urgency === 'high') {
        urgencyNote = ` With high urgency, I've leaned toward carriers hitting ${fastest.transitDays}-day transit.`;
    } else if (urgency === 'low') {
        urgencyNote = ` Since urgency is low, saving cash beats shaving a few days off transit.`;
    }

    const carrierNote = carrierPref && !['', 'no preference'].includes(carrierPref.toLowerCase())
        ? ` I also checked your carrier preference (${carrierPref}).`
        : '';

    const savingsNote = winner.savings > 0
        ? ` That saves you $${winner.savings}/container vs the contract rate — around $${(winner.savings * 15).toLocaleString()} across a 15-container shipment.`
        : '';

    const tradeoff = winner !== cheapest
        ? ` You could save another $${(winner.rate - cheapest.rate)}/container by switching to ${cheapest.carrier} (${cheapest.type}), but transit stretches to ${cheapest.transitDays} days.`
        : (winner !== fastest
            ? ` If you need it faster, ${fastest.carrier} ships in ${fastest.transitDays} days for $${fastest.rate}/container.`
            : '');

    return `For ${lane.origin} → ${lane.destination} (${lane.container} ${lane.commodity}), ${reason}.${urgencyNote}${carrierNote}${savingsNote}${tradeoff}`.trim();
};

const ROUTING_OPTIONS = [
    {
        id: 'direct',
        label: 'Direct Port Pair (USNYC → GBLIV)',
        description: 'Direct sailing, weekly schedule.',
        transitDays: 11,
        costDelta: 0,
        ports: 'New York → Liverpool',
        highlights: ['Weekly direct service', 'Minimal handling', 'On-time ~92%'],
    },
    {
        id: 'via-rotterdam',
        label: 'Transshipment via Rotterdam',
        description: 'Routes through NLRTM — broader carrier pool, occasional cost savings.',
        transitDays: 14,
        costDelta: -35,
        ports: 'New York → Rotterdam → Liverpool',
        highlights: ['Cheaper by ~$35/container', '+3 days transit', 'Best for non-urgent cargo'],
    },
];

const RateIntelligenceAgent = () => {
    const [lane, setLane] = useState(DEFAULT_LANE);
    const [laneConfirmed, setLaneConfirmed] = useState(false);
    const [moveType, setMoveType] = useState(null); // 'Door to Port' | 'Port to Door' | 'Port to Port'
    const [messages, setMessages] = useState(() => ([
        {
            id: 'intro',
            role: 'assistant',
            text: "Hi! I'm the Rate Intelligence Agent. First, choose your move type — then set up the lane and I'll pull live rates."
        },
        {
            id: 'move-type',
            role: 'assistant',
            kind: 'move-type',
        }
    ]));
    const [answers, setAnswers] = useState({});
    const [step, setStep] = useState(0);
    const [input, setInput] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [selectedRateIdx, setSelectedRateIdx] = useState(null);
    const [rankedRates, setRankedRates] = useState([]);
    const [insight, setInsight] = useState('');
    const [loadingAI, setLoadingAI] = useState(false);
    const [loadingRates, setLoadingRates] = useState(false);
    const [liveRates, setLiveRates] = useState(null); // raw rate-matrix response
    const [breakups, setBreakups] = useState([]);     // per-rate breakup [{ ocean, surcharges:[{code,desc,amount}], total }]
    const [rateError, setRateError] = useState(null);
    const [drayageInfo, setDrayageInfo] = useState(null); // { portCode, totalRate, miles, ramp_Port }
    const [drayageLoading, setDrayageLoading] = useState(false);
    const [allRates, setAllRates] = useState([]); // full pool from BOTH apis, sorted by rate, for "View All" modal
    const [showAllRates, setShowAllRates] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null); // index of row whose breakup is open
    const [allRatesFilter, setAllRatesFilter] = useState({ type: 'All', carrier: '', maxTransit: 0, minOnTime: 0 });
    const messagesEndRef = useRef(null);

    // If the API didn't return a real carrier name and the user picked one in the chat
    // (Q3 "preferred carrier"), use the user-picked carrier for display.
    const displayCarrier = (r) => {
        const picked = (answers.carrier || '').trim();
        const isPlaceholder = !r?.carrier || /^Carrier\s+\d+$/i.test(r.carrier);
        if (picked && picked.toLowerCase() !== 'no preference' && isPlaceholder) return picked;
        return r?.carrier || 'Carrier';
    };

    useEffect(() => {
        // Scroll only within the chat container — using `block: 'nearest'` prevents the
        // browser from scrolling ancestor elements (body/layout), which would push the
        // TopBar off-screen when new messages are appended.
        if (!messagesEndRef.current) return;
        const scrollable = messagesEndRef.current.closest('[data-chat-scroll]');
        if (scrollable) {
            scrollable.scrollTo({ top: scrollable.scrollHeight, behavior: 'smooth' });
        } else {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [messages, showResults]);

    // Map Rate Matrix API response → rate-card shape consumed by the existing ranking + UI
    const mapRatesFromMatrix = (data, apiContainerName) => {
        if (!Array.isArray(data) || data.length === 0) return { rates: [], breakups: [] };

        // One-time debug log so we can verify the real carrier field name in the API payload
        try {
            // eslint-disable-next-line no-console
            console.debug('[RateMatrix] sample rate keys =', Object.keys(data[0] || {}));
            // eslint-disable-next-line no-console
            console.debug('[RateMatrix] sample rate =', data[0]);
        } catch (_) { }

        // Round to 2 decimal places — keeps card prices clean ($760.57 instead of $760.56623)
        const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

        // Expanded carrier-name resolver — matches a wide range of API field names
        const resolveCarrier = (rate, idx) => {
            const candidates = [
                rate.Carrier, rate.CarrierName, rate.carrierName,
                rate.carrier, rate.carrier_name,
                rate.ServiceProvider, rate.ServiceProviderName,
                rate.OperatorName, rate.Operator,
                rate.LineName, rate.ShippingLine,
                rate.VesselOperator, rate.NVOCC,
                rate.CarrierSCAC, rate.carrierSCAC, rate.SCAC, rate.scac,
            ];
            for (const c of candidates) {
                if (c && String(c).trim()) return String(c).trim();
            }
            return `Carrier ${idx + 1}`;
        };

        // SCAC extractor — used to resolve real carrier name + logo via /api/ai/resolve-carriers
        const resolveScac = (rate) => {
            const candidates = [
                rate.Carrier_ScacCode, rate.CarrierScacCode, rate.carrier_scac_code,
                rate.CarrierSCAC, rate.carrierSCAC, rate.SCAC, rate.scac,
                rate.Scac, rate.ScacCode, rate.scacCode,
            ];
            for (const c of candidates) {
                if (c && String(c).trim()) return String(c).trim().toUpperCase();
            }
            return null;
        };

        const rates = [];
        const breakups = [];

        data.forEach((rate, idx) => {
            const ptp = rate.PortToPort || {};
            const containerDetails = ptp.ContainerDetails || [];
            const surcharges = ptp.FCLDefineOtherAddtional || [];

            const oceanRow = containerDetails.find(c => c.ContainerName === apiContainerName);
            const ocean = oceanRow ? r2(oceanRow.ContainerRate) : 0;
            if (ocean <= 0) return; // skip rates that don't have a price for this container

            const surchargeBreakup = [];
            let surchargeTotal = 0;
            surcharges.forEach((s) => {
                const cd = (s.ContainerDetails || []).find(c => c.ContainerName === apiContainerName);
                const amt = cd ? r2(cd.ContainerRate) : 0;
                if (amt > 0) {
                    surchargeBreakup.push({
                        code: s.SurchargeCode || s.Description || 'Surcharge',
                        desc: s.Description || s.SurchargeCode || '',
                        amount: amt,
                    });
                    surchargeTotal = r2(surchargeTotal + amt);
                }
            });

            const total = r2(ocean + surchargeTotal);
            const transit = parseInt(rate.TransitTime, 10) || parseInt(ptp.TransitTime, 10) || 14;
            const carrier = resolveCarrier(rate, idx);
            const scac = resolveScac(rate);
            const type = rate.RateType || rate.ContractType || (rate.IsContract ? 'Contract' : 'Spot');

            rates.push({
                type: type || 'Spot',
                carrier,
                scac,
                rate: total, // total per container so existing ranker still works on apples-to-apples basis
                ocean,
                transitDays: transit,
                reliability: Math.round(85 + Math.random() * 12),
            });
            breakups.push({
                ocean,
                surcharges: surchargeBreakup,
                total,
                carrier,
                scac,
                type: type || 'Spot',
            });
        });

        // Sort lowest total first and keep top 6
        const order = rates
            .map((r, i) => ({ r, b: breakups[i] }))
            .sort((a, b) => a.r.rate - b.r.rate)
            .slice(0, 6);
        return {
            rates: order.map(o => o.r),
            breakups: order.map(o => o.b),
        };
    };

    const fetchLiveRates = async () => {
        const polCode = (lane.originCode || '').replace(/\s+/g, '').toUpperCase();
        const podCode = (lane.destinationCode || '').replace(/\s+/g, '').toUpperCase();
        if (!polCode || !podCode) return null;

        const containerCfg = CONTAINER_API_MAP[lane.container] || CONTAINER_API_MAP['40HQ'];
        try {
            const res = await fetch(`${RATE_API_URL}/api/rate-matrix/search`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pol_code: polCode,
                    pod_code: podCode,
                    move_type: 'port/port',
                    container_types: [containerCfg.value],
                }),
            });
            const data = await res.json();
            if (!data?.success || !Array.isArray(data.data) || data.data.length === 0) {
                return { rates: [], breakups: [], error: data?.detail || 'No rates available for this lane.' };
            }
            const mapped = mapRatesFromMatrix(data.data, containerCfg.apiName);
            if (mapped.rates.length === 0) {
                return { rates: [], breakups: [], error: `No ${lane.container} rates found for ${polCode} → ${podCode}.` };
            }

            // Resolve SCAC → carrier name + logo domain (one batched call, cached server-side)
            const uniqueScacs = [...new Set(mapped.rates.map(r => r.scac).filter(Boolean))];
            if (uniqueScacs.length > 0) {
                try {
                    const resolveRes = await fetch(`${API_URL}/api/ai/resolve-carriers`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ scacs: uniqueScacs }),
                    });
                    const resolveData = await resolveRes.json();
                    const lookup = (resolveData && resolveData.results) || {};
                    mapped.rates.forEach((r) => {
                        if (r.scac && lookup[r.scac]) {
                            r.carrier = lookup[r.scac].name || r.carrier;
                            r.carrierDomain = lookup[r.scac].domain || null;
                        }
                    });
                    mapped.breakups.forEach((b) => {
                        if (b.scac && lookup[b.scac]) {
                            b.carrier = lookup[b.scac].name || b.carrier;
                            b.carrierDomain = lookup[b.scac].domain || null;
                        }
                    });
                } catch (resolveErr) {
                    console.warn('Carrier SCAC resolve failed:', resolveErr);
                }
            }

            return mapped;
        } catch (err) {
            console.error('Rate fetch error:', err);
            return { rates: [], breakups: [], error: 'Failed to reach the rate service.' };
        }
    };

    // Compose the 3-bucket card layout: Contract (existing) / Spot (cargorates) / ShipEz (existing)
    // + add drayage trucking cost to each card when applicable.
    const composeBuckets = (existingMapped, cargoratesSpot, drayage) => {
        const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
        const existing = existingMapped?.rates || [];
        const existingBks = existingMapped?.breakups || [];
        const truckTotal = r2(drayage?.totalRate || 0);
        const truckLine = truckTotal > 0
            ? { code: 'BYC Trucking', desc: drayage?.ramp_Port || 'Drayage', amount: truckTotal }
            : null;

        // Helper: add trucking to a card+breakup pair
        const withTruck = (rate, bk, typeLabel) => {
            const newRate = { ...rate, type: typeLabel, trucking: truckTotal, rate: r2((rate?.rate || 0) + truckTotal) };
            const newBk = {
                ...bk,
                type: typeLabel,
                trucking: truckTotal,
                surcharges: truckLine ? [...(bk?.surcharges || []), truckLine] : (bk?.surcharges || []),
                total: r2((bk?.total || 0) + truckTotal),
            };
            return { rate: newRate, bk: newBk };
        };

        const cards = [];
        const bks = [];

        // 1) Contract bucket → existing API first rate (cheapest after sort)
        if (existing.length > 0) {
            const { rate, bk } = withTruck(existing[0], existingBks[0], 'Contract');
            cards.push(rate); bks.push(bk);
        }

        // 2) Spot bucket → cargorates.ai cheapest spot
        if (cargoratesSpot) {
            const oceanWithCharges = r2(cargoratesSpot.rate);
            const surchargesArr = (cargoratesSpot.surcharges || []).map(s => ({ ...s, amount: r2(s.amount) }));
            const rateObj = {
                type: 'Spot',
                carrier: cargoratesSpot.carrier,
                scac: cargoratesSpot.scac,
                carrierDomain: cargoratesSpot.carrierDomain || null,
                rate: r2(oceanWithCharges + truckTotal),
                ocean: r2(cargoratesSpot.ocean || oceanWithCharges),
                transitDays: cargoratesSpot.transitDays,
                reliability: 92,
                trucking: truckTotal,
                source: 'cargorates',
            };
            const bkObj = {
                ocean: r2(cargoratesSpot.ocean || oceanWithCharges),
                surcharges: truckLine ? [...surchargesArr, truckLine] : surchargesArr,
                total: r2(oceanWithCharges + truckTotal),
                carrier: cargoratesSpot.carrier,
                scac: cargoratesSpot.scac,
                carrierDomain: cargoratesSpot.carrierDomain || null,
                type: 'Spot',
                trucking: truckTotal,
                source: 'cargorates',
            };
            cards.push(rateObj); bks.push(bkObj);
        } else if (existing.length > 1) {
            // Fallback: take 2nd existing rate as Spot
            const { rate, bk } = withTruck(existing[1], existingBks[1], 'Spot');
            cards.push(rate); bks.push(bk);
        }

        // 3) ShipEz bucket → next existing rate (the one after the contract pick, skipping the spot pick)
        const shipezIdx = cargoratesSpot ? 1 : 2;
        if (existing.length > shipezIdx) {
            const { rate, bk } = withTruck(existing[shipezIdx], existingBks[shipezIdx], 'ShipEz');
            cards.push(rate); bks.push(bk);
        } else if (existing.length > 0 && cards.length < 3) {
            // Last-resort fallback: reuse first existing
            const { rate, bk } = withTruck(existing[0], existingBks[0], 'ShipEz');
            cards.push(rate); bks.push(bk);
        }

        return { rates: cards, breakups: bks };
    };

    // Parse cargorates string numbers like "1,825.00" → 1825
    const parseRateStr = (v) => {
        if (v == null) return 0;
        const n = Number(String(v).replace(/,/g, '').trim());
        return isFinite(n) ? n : 0;
    };

    // Hit drayage API → auto-fill port code + cache trucking cost
    const fetchDrayage = async (zip, mt) => {
        if (!zip || !zip.trim()) return;
        const moveType = mt === 'Port to Door' ? 'import' : 'export';
        try {
            console.log("==============================call drayage api===============================");
            setDrayageLoading(true);
            const r = await fetch(`${API_URL}/api/cargorates/drayage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pickuplocation_ramp_port_zip: zip.trim(),
                    miles: '200',
                    movetype: moveType,
                }),
            });
            const data = await r.json();
            if (!r.ok || !Array.isArray(data) || data.length === 0) {
                toast.error(data?.detail || 'No drayage rates found for this zip');
                setDrayageInfo(null);
                return;
            }
            // Pick the entry with the lowest miles (closest port)
            const cheapest = [...data].sort((a, b) => parseRateStr(a.miles) - parseRateStr(b.miles))[0];
            const info = {
                portCode: cheapest.portCode,
                ramp_Port: cheapest.ramp_Port,
                miles: cheapest.miles,
                totalRate: parseRateStr(cheapest.totalRate),
            };
            setDrayageInfo(info);

            // Resolve port code → city name via LLM-backed resolver, then auto-fill both code and city
            let portCity = info.portCode;
            try {
                const rp = await fetch(`${API_URL}/api/cargorates/resolve-ports`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ codes: [info.portCode] }),
                });
                const rpData = await rp.json();
                const resolved = rpData?.results?.[info.portCode]?.name;
                if (resolved) portCity = resolved;
            } catch (rpErr) {
                console.warn('Port name resolve failed:', rpErr);
            }

            // Auto-fill BOTH the port code and the city name on the right side of the lane
            setLane(l => mt === 'Port to Door'
                ? { ...l, destinationCode: info.portCode, destination: portCity }
                : { ...l, originCode: info.portCode, origin: portCity }
            );
            toast.success(`Drayage: ${info.ramp_Port} · ${info.miles} mi · $${info.totalRate.toFixed(2)}`);
        } catch (e) {
            console.error('drayage fetch error:', e);
            toast.error('Failed to fetch drayage rate');
            setDrayageInfo(null);
        } finally {
            setDrayageLoading(false);
        }
    };

    // Fetch the cheapest SPOT rate from cargorates.ai for the "Spot" card slot
    const fetchCargoratesSpot = async () => {
        const polCode = (lane.originCode || '').replace(/\s+/g, '').toUpperCase();
        const podCode = (lane.destinationCode || '').replace(/\s+/g, '').toUpperCase();
        const containerCfg = CONTAINER_API_MAP[lane.container] || CONTAINER_API_MAP['40HQ'];
        // Map our UI move type → cargorates move type
        const mtMap = {
            'Port to Port': 'port/port',
            'Door to Port': 'door/port',
            'Port to Door': 'port/door',
        };
        const apiMoveType = mtMap[moveType] || 'port/port';
        // Departure date = today + 7 days in "DD MMM YYYY" format
        const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const departure = `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
        try {
            const r = await fetch(`${API_URL}/api/cargorates/spot-rate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    OriginUnloc: polCode,
                    DestinationUnLoc: podCode,
                    ContainerType: containerCfg.value,
                    DepartureDate: departure,
                    MoveType: apiMoveType,
                }),
            });
            const data = await r.json();
            if (!r.ok || !Array.isArray(data?.ratesInfo) || data.ratesInfo.length === 0) {
                return null;
            }
            // Pick the cheapest spot rate
            const flat = data.ratesInfo.map((ri) => {
                const charges = ri.containerWiseTotalCharges?.[0] || {};
                const breakup = ri.containerWiseChargeBreakups?.[0] || {};
                const ocean = Number(breakup?.oceanFreight?.subTotal || 0);
                const surcharges = [];
                (breakup?.oceanFreight?.primaryCharge || []).forEach(c => {
                    if (c.chargeDescription && c.chargeDescription.toLowerCase() !== 'basic ocean freight') {
                        surcharges.push({ code: c.chargeDescription, desc: c.chargeType || '', amount: Number(c.amount || 0) });
                    }
                });
                (breakup?.destinationCharge?.destinationCharges || []).forEach(c => {
                    surcharges.push({ code: c.chargeDescription, desc: c.chargeType || 'Destination', amount: Number(c.amount || 0) });
                });
                (breakup?.originCharge?.originCharges || []).forEach(c => {
                    surcharges.push({ code: c.chargeDescription, desc: c.chargeType || 'Origin', amount: Number(c.amount || 0) });
                });
                return {
                    carrier: ri.primaryDetail?.carrierName || ri.carrierName || 'Carrier',
                    scac: ri.primaryDetail?.carrierScac || null,
                    transitDays: parseInt(ri.primaryDetail?.transitTime, 10) || 14,
                    rate: Number(charges.totalAmount || 0),
                    ocean,
                    surcharges,
                    rateId: ri.primaryDetail?.rateId,
                };
            }).filter(x => x.rate > 0);
            if (flat.length === 0) return null;
            flat.sort((a, b) => a.rate - b.rate);
            const picked = flat[0];

            // Resolve SCAC → real carrier name + favicon domain (same path used for rate-matrix)
            const uniqueScacs = [...new Set(flat.map(f => f.scac).filter(Boolean))];
            if (uniqueScacs.length > 0) {
                try {
                    const rRes = await fetch(`${API_URL}/api/ai/resolve-carriers`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ scacs: uniqueScacs }),
                    });
                    const rData = await rRes.json();
                    const lookup = rData?.results || {};
                    flat.forEach(f => {
                        if (f.scac && lookup[f.scac]) {
                            if (lookup[f.scac].name) f.carrier = lookup[f.scac].name;
                            if (lookup[f.scac].domain) f.carrierDomain = lookup[f.scac].domain;
                        }
                    });
                } catch (resolveErr) {
                    console.warn('cargorates SCAC resolve failed:', resolveErr);
                }
            }
            // Return cheapest as the "picked" + the full flat list (so we can show "View All")
            return { picked, all: flat };
        } catch (e) {
            console.warn('cargorates spot fetch failed:', e);
            return null;
        }
    };

    const selectMoveType = (mt) => {
        setMoveType(mt);
        // If Port to Door, pre-fill destination hint to USA so user knows
        if (mt === 'Port to Door') {
            setLane(l => ({ ...l, destination: '', destinationCode: '' }));
        }
        setMessages(prev => [
            ...prev,
            { id: `move-type-pick-${Date.now()}`, role: 'user', text: `Move type: ${mt}` },
            {
                id: 'lane-setup',
                role: 'assistant',
                kind: 'lane-setup',
                text: mt === 'Port to Door'
                    ? "Got it — Port to Door. Please enter your USA destination address/port below."
                    : `Got it — ${mt}. Set up the lane below.`,
            },
        ]);
    };

    const confirmLane = async () => {
        if (!lane.origin?.trim() || !lane.destination?.trim() || !lane.container) {
            toast.error('Please fill in origin, destination and container type');
            return;
        }
        if (!lane.originCode?.trim() || !lane.destinationCode?.trim()) {
            toast.error('Please enter origin and destination port codes (e.g. USNYC, GBLIV)');
            return;
        }
        // Port to Door → destination must be a USA location (port code starts with "US")
        if (moveType === 'Port to Door' && !lane.destinationCode.trim().toUpperCase().startsWith('US')) {
            toast.error('For Port to Door, destination must be a USA location (port code should start with "US", e.g. USLAX, USNYC)');
            return;
        }
        setLaneConfirmed(true);
        setLoadingRates(true);
        setRateError(null);
        setMessages(prev => [
            ...prev,
            { id: 'lane-confirmed', role: 'user', text: `${moveType ? moveType + ' · ' : ''}${lane.origin} (${lane.originCode}) → ${lane.destination} (${lane.destinationCode}) · ${lane.container}${lane.commodity ? ' · ' + lane.commodity : ''}` },
            { id: 'fetching-rates', role: 'assistant', text: `Pulling live rates from the matrix for ${lane.originCode} → ${lane.destinationCode}…` },
        ]);

        // Fetch existing rate-matrix + cargorates SPOT in parallel
        const [result, cargoratesResult] = await Promise.all([
            fetchLiveRates(),
            fetchCargoratesSpot().catch(() => null),
        ]);
        setLoadingRates(false);

        const cargoratesSpot = cargoratesResult?.picked || null;
        const cargoratesAll = cargoratesResult?.all || [];

        // Compose Contract / Spot / ShipEz buckets, injecting drayage trucking if present
        const composed = composeBuckets(result, cargoratesSpot, drayageInfo);

        // Build the FULL pool (mixed: all existing rates + all cargorates spot rates) for the "View All" modal
        const truckTotal = drayageInfo?.totalRate || 0;
        const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
        const fullPool = [];
        const truckLine = truckTotal > 0
            ? { code: 'BYC Trucking', desc: drayageInfo?.ramp_Port || 'Drayage', amount: truckTotal }
            : null;
        (result?.rates || []).forEach((r, idx) => {
            const bk = result.breakups[idx] || {};
            const mergedBk = {
                ...bk,
                surcharges: truckLine ? [...(bk.surcharges || []), truckLine] : (bk.surcharges || []),
                total: r2((bk.total || 0) + truckTotal),
                trucking: truckTotal,
                carrier: bk.carrier || r.carrier,
                carrierDomain: bk.carrierDomain || r.carrierDomain || null,
            };
            fullPool.push({
                type: r.type === 'Contract' ? 'Contract' : 'ShipEz',
                carrier: r.carrier,
                carrierDomain: r.carrierDomain || null,
                scac: r.scac,
                rate: r2((r.rate || 0) + truckTotal),
                ocean: r.ocean,
                transitDays: r.transitDays,
                reliability: r.reliability,
                trucking: truckTotal,
                source: 'rate-matrix',
                _breakup: mergedBk,
            });
        });
        cargoratesAll.forEach((r) => {
            const surchargesArr = (r.surcharges || []);
            const withTruck = drayageInfo?.totalRate
                ? [...surchargesArr, { code: 'BYC Trucking', desc: drayageInfo?.ramp_Port || 'Drayage', amount: drayageInfo.totalRate }]
                : surchargesArr;
            fullPool.push({
                type: 'Spot',
                carrier: r.carrier,
                carrierDomain: r.carrierDomain || null,
                scac: r.scac,
                rate: r2((r.rate || 0) + truckTotal),
                ocean: r.ocean,
                transitDays: r.transitDays,
                reliability: 92,
                trucking: truckTotal,
                source: 'cargorates',
                _breakup: {
                    ocean: r.ocean,
                    surcharges: withTruck,
                    total: r2((r.rate || 0) + truckTotal),
                    carrier: r.carrier,
                    carrierDomain: r.carrierDomain || null,
                    type: 'Spot',
                },
            });
        });
        fullPool.sort((a, b) => a.rate - b.rate);
        setAllRates(fullPool);

        if (composed?.rates?.length) {
            setLiveRates(composed.rates);
            setBreakups(composed.breakups);
            const lowest = [...composed.rates].sort((a, b) => a.rate - b.rate)[0];
            const truckNote = drayageInfo?.totalRate ? ` (incl. trucking $${drayageInfo.totalRate.toFixed(2)})` : '';
            setMessages(prev => [
                ...prev,
                { id: 'rates-found', role: 'assistant', text: `Found ${composed.rates.length} live rates${truckNote}. Lowest: $${lowest.rate.toLocaleString()}/container. Now 3 quick questions to find the best fit.` },
                {
                    id: 'q-0',
                    role: 'assistant',
                    text: QUESTIONS[0].prompt,
                    options: QUESTIONS[0].options,
                    questionId: QUESTIONS[0].id,
                }
            ]);
        } else {
            // Fallback to demo rates
            setLiveRates(null);
            setBreakups([]);
            setRateError(result?.error || 'No live rates found.');
            setMessages(prev => [
                ...prev,
                { id: 'rates-fallback', role: 'assistant', text: `${result?.error || 'Rates unavailable'} — using indicative rates so you can still see how the agent works.` },
                {
                    id: 'q-0',
                    role: 'assistant',
                    text: QUESTIONS[0].prompt,
                    options: QUESTIONS[0].options,
                    questionId: QUESTIONS[0].id,
                }
            ]);
        }
    };

    const submitAnswer = async (value) => {
        const q = QUESTIONS[step];
        if (!q) return;

        // Append user reply + record answer
        const newAnswers = { ...answers, [q.id]: value };
        setAnswers(newAnswers);
        setMessages(prev => [...prev, { id: `u-${step}-${Date.now()}`, role: 'user', text: value }]);
        setInput('');

        const nextIdx = step + 1;
        if (nextIdx < QUESTIONS.length) {
            // Ask next question
            const nextQ = QUESTIONS[nextIdx];
            setTimeout(() => {
                setMessages(prev => [...prev, {
                    id: `q-${nextIdx}`,
                    role: 'assistant',
                    text: nextQ.prompt,
                    options: nextQ.options,
                    questionId: nextQ.id,
                    allowFreeText: nextQ.allowFreeText,
                }]);
                setStep(nextIdx);
            }, 280);
        } else {
            // All 3 answered — apply carrier filter, then rank + show
            setStep(nextIdx);
            setLoadingAI(true);

            const fullPool = liveRates && liveRates.length ? liveRates : DEMO_RATES;
            const fullBreakups = (liveRates && liveRates.length) ? breakups : [];
            const pickedCarrier = (newAnswers.carrier || '').trim();
            const isSpecificCarrier = pickedCarrier && pickedCarrier.toLowerCase() !== 'no preference';

            // Filter to picked carrier (case-insensitive substring match on name OR SCAC)
            let filteredPool = fullPool;
            let filteredBreakups = fullBreakups;
            if (isSpecificCarrier) {
                const needle = pickedCarrier.toLowerCase();
                const keepIdx = [];
                fullPool.forEach((r, i) => {
                    const nameMatch = (r.carrier || '').toLowerCase().includes(needle);
                    const scacMatch = (r.scac || '').toLowerCase().includes(needle);
                    if (nameMatch || scacMatch) keepIdx.push(i);
                });
                filteredPool = keepIdx.map(i => fullPool[i]);
                filteredBreakups = keepIdx.map(i => fullBreakups[i]).filter(Boolean);
            }

            // No carrier match — bail with friendly empty state
            if (isSpecificCarrier && filteredPool.length === 0) {
                setRankedRates([]);
                setSelectedRateIdx(null);
                setBreakups([]);
                setInsight(`No live ${pickedCarrier} rates were returned for ${lane.origin} → ${lane.destination} on ${lane.container}. Try another carrier or choose "No preference" to see all available options.`);
                setMessages(prev => [...prev, {
                    id: 'no-carrier-rates',
                    role: 'assistant',
                    text: `Rates not found for ${pickedCarrier} on this lane.`,
                }]);
                setLoadingAI(false);
                setShowResults(true);
                return;
            }

            // For "No preference" — keep top 3 cheapest. Otherwise keep all carrier matches (still capped at 3).
            const finalPool = [...filteredPool].sort((a, b) => a.rate - b.rate).slice(0, 3);
            const finalBreakups = finalPool.map(r => filteredBreakups[filteredPool.indexOf(r)]).filter(Boolean);
            setBreakups(finalBreakups);

            const ranked = rankRates(finalPool, newAnswers);
            setRankedRates(ranked);
            setSelectedRateIdx(ranked.findIndex(r => r.recommended));

            // Offline fallback insight
            const localInsight = buildInsight(ranked, newAnswers, lane);

            // Try to enrich with LLM
            try {
                const prompt = `User answers: urgency=${newAnswers.urgency}, priority=${newAnswers.priority}, preferredCarrier=${newAnswers.carrier}. Lane: ${lane.origin}→${lane.destination} ${lane.container} ${lane.commodity || ''}. Ranked rates: ${JSON.stringify(ranked.map(r => ({ carrier: r.carrier, type: r.type, rate: r.rate, transit: r.transitDays, tags: r.tags, recommended: r.recommended })))}. In 3-4 sentences, explain why the recommended rate is best — reference user inputs, call out tradeoffs (cost vs transit vs reliability), and use plain friendly English.`;
                const res = await fetch(`${API_URL}/api/ai/rate-search/chat`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: `rate-intel-${Date.now()}`, message: prompt }),
                });
                const data = await res.json();
                setInsight((data?.reply || '').trim() || localInsight);
            } catch (err) {
                setInsight(localInsight);
            }
            setLoadingAI(false);
            setShowResults(true);
        }
    };

    const handleFreeText = () => {
        const text = input.trim();
        if (!text) return;
        submitAnswer(text);
    };

    const resetChat = () => {
        setAnswers({});
        setStep(0);
        setShowResults(false);
        setSelectedRateIdx(null);
        setRankedRates([]);
        setInsight('');
        setInput('');
        setLane(DEFAULT_LANE);
        setLaneConfirmed(false);
        setMoveType(null);
        setLiveRates(null);
        setBreakups([]);
        setRateError(null);
        setDrayageInfo(null);
        setAllRates([]);
        setShowAllRates(false);
        setExpandedRow(null);
        setMessages([
            {
                id: 'intro',
                role: 'assistant',
                text: "Hi! I'm the Rate Intelligence Agent. First, choose your move type — then set up the lane and I'll pull live rates."
            },
            {
                id: 'move-type',
                role: 'assistant',
                kind: 'move-type',
            }
        ]);
    };

    const currentQuestion = QUESTIONS[step];
    const selectedRate = selectedRateIdx != null ? rankedRates[selectedRateIdx] : null;

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-slate-950 p-4 sm:p-6" data-testid="rate-intelligence-page">
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 via-sky-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">Rate Intelligence Agent</h1>
                        <p className="text-sm text-slate-500">Mark · AI-powered rate discovery for your next shipment</p>
                    </div>
                </div>
                <button
                    onClick={resetChat}
                    className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                    data-testid="reset-chat-btn"
                >
                    <RefreshCw className="w-4 h-4" /> Start over
                </button>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-5 lg:items-stretch">
                {/* LEFT: chat */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col min-h-[480px] h-[calc(100dvh-var(--navbar-height,80px)-140px)]">
                    <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-indigo-500/10 via-sky-500/10 to-cyan-500/10 flex items-center gap-2">
                        <Bot className="w-5 h-5 text-indigo-500" />
                        <div>
                            <p className="font-semibold text-slate-700 dark:text-white">Chat</p>
                            <p className="text-xs text-slate-500">3 quick questions — then rates</p>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4" data-testid="chat-messages" data-chat-scroll>
                        {messages.map((m, mIdx) => (
                            <div key={m.id} className={`flex items-start gap-2.5 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                {m.role === 'assistant' ? (
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow">
                                        <Bot className="w-4 h-4 text-white" />
                                    </div>
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                                        <UserIcon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                                    </div>
                                )}
                                <div className={`max-w-[85%] space-y-2 ${m.role === 'user' ? 'items-end' : 'items-start'} flex flex-col`}>
                                    {m.kind === 'move-type' ? (
                                        <div className="rounded-2xl rounded-tl-sm bg-slate-100 dark:bg-slate-800 p-3.5 w-full max-w-md" data-testid="move-type-card">
                                            <p className="text-xs uppercase tracking-wider text-indigo-500 font-semibold mb-2">Choose your move type</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">How will the cargo move between origin and destination?</p>
                                            <div className="grid grid-cols-1 gap-2">
                                                {MOVE_TYPES.map(mt => {
                                                    const active = moveType === mt.label;
                                                    return (
                                                        <button
                                                            key={mt.id}
                                                            onClick={() => !moveType && selectMoveType(mt.label)}
                                                            disabled={!!moveType}
                                                            data-testid={`move-type-${mt.id}`}
                                                            className={`text-left px-3 py-2 rounded-lg border transition ${active
                                                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 ring-2 ring-indigo-500/30'
                                                                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-400 hover:bg-indigo-50/40 dark:hover:bg-indigo-500/10'
                                                                } ${moveType && !active ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        >
                                                            <p className={`text-sm font-semibold ${active ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-white'}`}>
                                                                {mt.label}
                                                            </p>
                                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{mt.description}</p>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : m.kind === 'lane-setup' ? (
                                        <div className="rounded-2xl rounded-tl-sm bg-slate-100 dark:bg-slate-800 p-3.5 w-full max-w-md" data-testid="lane-setup-card">
                                            {m.text && (
                                                <p className="text-xs text-slate-600 dark:text-slate-300 mb-2.5">{m.text}</p>
                                            )}
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-xs uppercase tracking-wider text-indigo-500 font-semibold">Set up your lane</p>
                                                {moveType && (
                                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300" data-testid="lane-move-type-badge">
                                                        {moveType}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="space-y-2.5">
                                                {(moveType === 'Door to Port' || moveType === 'Port to Door') && (
                                                    <div className="pb-2.5 border-b border-slate-200 dark:border-slate-700">
                                                        <label className="block text-[11px] font-medium text-slate-500 mb-1">
                                                            {moveType === 'Door to Port' ? 'Pickup door zip (USA)' : 'Delivery door zip (USA)'}
                                                        </label>
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                value={lane.doorZip}
                                                                onChange={(e) => setLane(l => ({ ...l, doorZip: e.target.value.trim() }))}
                                                                onBlur={(e) => {
                                                                    const z = e.target.value.trim();
                                                                    if (z && z !== drayageInfo?._lastZip) {
                                                                        fetchDrayage(z, moveType);
                                                                        setDrayageInfo((d) => d ? { ...d, _lastZip: z } : null);
                                                                    }
                                                                }}
                                                                disabled={laneConfirmed || drayageLoading}
                                                                placeholder="e.g. 33010"
                                                                data-testid="lane-door-zip"
                                                                className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => fetchDrayage(lane.doorZip, moveType)}
                                                                disabled={!lane.doorZip || laneConfirmed || drayageLoading}
                                                                data-testid="lane-door-zip-lookup"
                                                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                                                            >
                                                                {drayageLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Anchor className="w-3 h-3" />}
                                                                Lookup
                                                            </button>
                                                        </div>
                                                        {drayageInfo && (
                                                            <div className="mt-2 px-2.5 py-1.5 rounded-md bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-[11px] text-emerald-700 dark:text-emerald-300" data-testid="drayage-info">
                                                                <p className="font-semibold">Nearest port: <span className="font-mono">{drayageInfo.portCode}</span></p>
                                                                <p className="text-emerald-600 dark:text-emerald-400">{drayageInfo.ramp_Port} · {drayageInfo.miles} mi · Trucking ${drayageInfo.totalRate.toFixed(2)}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="col-span-2">
                                                        <label className="block text-[11px] font-medium text-slate-500 mb-1">Origin port / city</label>
                                                        <input
                                                            type="text"
                                                            value={lane.origin}
                                                            onChange={(e) => setLane(l => ({ ...l, origin: e.target.value }))}
                                                            disabled={laneConfirmed}
                                                            placeholder="e.g. New York"
                                                            data-testid="lane-origin"
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[11px] font-medium text-slate-500 mb-1">POL code</label>
                                                        <input
                                                            type="text"
                                                            value={lane.originCode}
                                                            onChange={(e) => setLane(l => ({ ...l, originCode: e.target.value.toUpperCase() }))}
                                                            disabled={laneConfirmed}
                                                            placeholder="USNYC"
                                                            data-testid="lane-origin-code"
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60 uppercase tracking-wider"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="col-span-2">
                                                        <label className="block text-[11px] font-medium text-slate-500 mb-1">
                                                            {moveType === 'Port to Door' ? 'Destination (USA city / door)' : 'Destination port / city'}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={lane.destination}
                                                            onChange={(e) => setLane(l => ({ ...l, destination: e.target.value }))}
                                                            disabled={laneConfirmed}
                                                            placeholder={moveType === 'Port to Door' ? 'e.g. Chicago, IL (USA)' : 'e.g. Liverpool'}
                                                            data-testid="lane-destination"
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[11px] font-medium text-slate-500 mb-1">
                                                            {moveType === 'Port to Door' ? 'USA port code' : 'POD code'}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={lane.destinationCode}
                                                            onChange={(e) => setLane(l => ({ ...l, destinationCode: e.target.value.toUpperCase() }))}
                                                            disabled={laneConfirmed}
                                                            placeholder={moveType === 'Port to Door' ? 'USLAX' : 'GBLIV'}
                                                            data-testid="lane-destination-code"
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60 uppercase tracking-wider"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="block text-[11px] font-medium text-slate-500 mb-1">Container type</label>
                                                        <select
                                                            value={lane.container}
                                                            onChange={(e) => setLane(l => ({ ...l, container: e.target.value }))}
                                                            disabled={laneConfirmed}
                                                            data-testid="lane-container"
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60"
                                                        >
                                                            {CONTAINER_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-[11px] font-medium text-slate-500 mb-1">Commodity (optional)</label>
                                                        <input
                                                            type="text"
                                                            value={lane.commodity}
                                                            onChange={(e) => setLane(l => ({ ...l, commodity: e.target.value }))}
                                                            disabled={laneConfirmed}
                                                            placeholder="e.g. Newsprint"
                                                            data-testid="lane-commodity"
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {!laneConfirmed && (
                                                <button
                                                    onClick={confirmLane}
                                                    disabled={loadingRates}
                                                    data-testid="lane-confirm-btn"
                                                    className="mt-3 w-full px-3 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-600 hover:to-cyan-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                                >
                                                    {loadingRates ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 animate-spin" /> Fetching rates…
                                                        </>
                                                    ) : (
                                                        'Continue'
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${m.role === 'user'
                                            ? 'bg-indigo-600 text-white rounded-tr-sm'
                                            : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white rounded-tl-sm'
                                            }`}>
                                            {m.text}
                                        </div>
                                    )}
                                    {m.options && mIdx === messages.length - 1 && !showResults && (
                                        <div className="flex flex-wrap gap-2">
                                            {m.options.map((opt, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => submitAnswer(opt)}
                                                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 border border-indigo-100 dark:border-indigo-500/20"
                                                    data-testid={`option-${m.questionId}-${i}`}
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {loadingAI && (
                            <div className="flex items-start gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div className="px-3.5 py-2.5 rounded-2xl bg-slate-100 dark:bg-slate-800 rounded-tl-sm flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input — only active on free-text questions, disabled after results */}
                    {currentQuestion?.allowFreeText && !showResults && (
                        <div className="border-t border-slate-100 dark:border-slate-800 p-3 bg-slate-50 dark:bg-slate-900">
                            <div className="flex items-end gap-2">
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleFreeText(); } }}
                                    placeholder="Type a carrier name..."
                                    data-testid="chat-input"
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                                />
                                <button
                                    onClick={handleFreeText}
                                    disabled={!input.trim()}
                                    className="p-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-600 hover:to-cyan-600 text-white disabled:opacity-50"
                                >
                                    <Send className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT: results panel */}
                <div className="lg:col-span-3 space-y-5 overflow-y-auto pr-1 min-h-[480px] h-[calc(100dvh-var(--navbar-height,80px)-140px)]">
                    {!showResults ? (
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-8 text-center" data-testid="waiting-panel">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 via-sky-500/20 to-cyan-500/20 mx-auto mb-4 flex items-center justify-center">
                                <Sparkles className="w-8 h-8 text-indigo-500" />
                            </div>
                            <h2 className="text-lg font-semibold text-slate-800 dark:text-white mb-1">Your rate recommendation will appear here</h2>
                            <p className="text-sm text-slate-500 max-w-md mx-auto">Answer the 3 quick questions on the left. I'll pull rates from our matrix, compare Contract · Spot · ShipEz, and recommend the best fit for your shipment.</p>
                            <div className="flex items-center justify-center gap-4 mt-6 text-xs text-slate-400">
                                <span className="flex items-center gap-1"><Ship className="w-4 h-4" /> {lane.origin} → {lane.destination}</span>
                                <span className="flex items-center gap-1"><Anchor className="w-4 h-4" /> {lane.container} {lane.commodity}</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Lane summary */}
                            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                                {moveType && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300" data-testid="summary-move-type">
                                        {moveType}
                                    </span>
                                )}
                                <span className="flex items-center gap-2 text-slate-700 dark:text-white font-medium">
                                    <Ship className="w-4 h-4 text-indigo-500" />
                                    {lane.origin} → {lane.destination}
                                </span>
                                <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                    <Anchor className="w-4 h-4 text-slate-400" />
                                    {lane.container} · {lane.commodity || '—'}
                                </span>
                                <span className="text-xs text-slate-500">Urgency: <b className="text-slate-700 dark:text-white">{answers.urgency}</b></span>
                                <span className="text-xs text-slate-500">Priority: <b className="text-slate-700 dark:text-white">{answers.priority}</b></span>
                                <span className="text-xs text-slate-500">Carrier: <b className="text-slate-700 dark:text-white">{answers.carrier || 'Any'}</b></span>
                            </div>

                            {/* Empty state — picked carrier has no rates on this lane */}
                            {rankedRates.length === 0 ? (
                                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-10 text-center" data-testid="rates-not-found">
                                    <div className="w-14 h-14 rounded-full mx-auto mb-4 bg-rose-50 dark:bg-rose-500/10 flex items-center justify-center">
                                        <Ship className="w-7 h-7 text-rose-500" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">Rates not found</h3>
                                    <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                                        No live <b>{answers.carrier}</b> rates were returned for {lane.origin} → {lane.destination} on {lane.container}. Try another carrier or pick <b>No preference</b> to see all available options.
                                    </p>
                                    <button
                                        onClick={resetChat}
                                        data-testid="restart-search-btn"
                                        className="mt-5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold inline-flex items-center gap-2"
                                    >
                                        <RefreshCw className="w-4 h-4" /> Start a new search
                                    </button>
                                </div>
                            ) : (
                                /* 3 Rate Cards */
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="rate-cards">
                                    {rankedRates.map((r, idx) => {
                                        const isSelected = selectedRateIdx === idx;
                                        return (
                                            <motion.div
                                                key={idx}
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.08 }}
                                                onClick={() => setSelectedRateIdx(idx)}
                                                data-testid={`rate-card-${idx}`}
                                                className={`cursor-pointer relative rounded-2xl border-2 p-5 bg-white dark:bg-slate-900 shadow-sm transition-all ${isSelected
                                                    ? 'border-indigo-500 shadow-lg shadow-indigo-500/20 scale-[1.02]'
                                                    : 'border-slate-200 dark:border-slate-800 hover:border-indigo-200 dark:hover:border-indigo-500/30'
                                                    }`}
                                            >
                                                {r.recommended && (
                                                    <span className="absolute -top-3 left-4 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r from-indigo-500 to-cyan-500 text-white shadow">
                                                        ✨ Recommended
                                                    </span>
                                                )}
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <CarrierLogo carrier={displayCarrier(r)} domain={r.carrierDomain} size={40} />
                                                        <div className="min-w-0">
                                                            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{r.type}</p>
                                                            <p className="text-lg font-bold text-slate-800 dark:text-white truncate" title={displayCarrier(r)}>{displayCarrier(r)}</p>
                                                        </div>
                                                    </div>
                                                    {isSelected && <CheckCircle2 className="w-5 h-5 text-indigo-500 flex-shrink-0" />}
                                                </div>
                                                <div className="flex items-baseline gap-1 mb-3">
                                                    <span className="text-3xl font-bold text-slate-800 dark:text-white">${fmtUSD(r.rate)}</span>
                                                    <span className="text-xs text-slate-500">/ container</span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                                                    <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {r.transitDays} days</span>
                                                    <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {r.reliability}% on-time</span>
                                                </div>
                                                {r.savings > 0 && (
                                                    <div className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-3">
                                                        <TrendingDown className="w-3 h-3" />
                                                        Saves ${r.savings}/container vs contract
                                                    </div>
                                                )}
                                                {r.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {r.tags.map((tag, tIdx) => {
                                                            const tagCfg = {
                                                                'Best Price': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
                                                                'Fastest': 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
                                                                'Recommended': 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/20 dark:text-fuchsia-400',
                                                            }[tag] || 'bg-slate-100 text-slate-600';
                                                            return (
                                                                <span key={tIdx} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${tagCfg}`}>{tag}</span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* View all rates link — only when we have a mixed pool */}
                            {rankedRates.length > 0 && allRates.length > 3 && (
                                <div className="flex justify-end">
                                    <button
                                        onClick={() => setShowAllRates(true)}
                                        data-testid="view-all-rates-btn"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20"
                                    >
                                        View all {allRates.length} rates
                                        <ChevronRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}

                            {/* Rate Breakup — only when live rates are loaded */}
                            {rankedRates.length > 0 && liveRates && liveRates.length > 0 && selectedRate && breakups[selectedRateIdx] && (() => {
                                const bk = breakups[selectedRateIdx];
                                return (
                                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="rate-breakup-card">
                                        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-emerald-50 to-cyan-50 dark:from-emerald-500/10 dark:to-cyan-500/10 flex items-center justify-between">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <CarrierLogo carrier={displayCarrier(bk)} domain={bk.carrierDomain} size={32} />
                                                <div className="min-w-0">
                                                    <h3 className="text-sm font-bold text-slate-700 dark:text-white truncate">{displayCarrier(bk)}</h3>
                                                    <p className="text-[11px] text-slate-500">Rate Breakup · {bk.type} · per {lane.container}</p>
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/20 flex-shrink-0">Live</span>
                                        </div>
                                        <div className="p-5">
                                            {/* Ocean freight */}
                                            <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                                                <div className="flex items-center gap-2">
                                                    <Ship className="w-4 h-4 text-indigo-500" />
                                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-100">Ocean Freight</span>
                                                </div>
                                                <span className="text-sm font-bold text-slate-800 dark:text-white tabular-nums">${fmtUSD(bk.ocean)}</span>
                                            </div>

                                            {/* Surcharges */}
                                            {bk.surcharges.length > 0 && (
                                                <div className="py-2">
                                                    <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2">Surcharges ({bk.surcharges.length})</p>
                                                    <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                                                        {bk.surcharges.map((s, i) => (
                                                            <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                                <div className="min-w-0 flex-1">
                                                                    <span className="font-semibold text-slate-700 dark:text-slate-200">{s.code}</span>
                                                                    {s.desc && s.desc !== s.code && (
                                                                        <span className="text-slate-400 ml-1.5 truncate">· {s.desc.slice(0, 40)}{s.desc.length > 40 ? '…' : ''}</span>
                                                                    )}
                                                                </div>
                                                                <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums flex-shrink-0">${fmtUSD(s.amount)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Subtotals */}
                                            <div className="border-t border-slate-100 dark:border-slate-800 pt-3 space-y-1.5">
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-500">Ocean subtotal</span>
                                                    <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">${fmtUSD(bk.ocean)}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-500">Surcharges total</span>
                                                    <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">${fmtUSD(bk.total - bk.ocean)}</span>
                                                </div>
                                            </div>

                                            {/* Grand total */}
                                            <div className="mt-3 p-3 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white flex items-center justify-between shadow-md shadow-indigo-500/20">
                                                <div>
                                                    <p className="text-[11px] uppercase tracking-wider opacity-90 font-semibold">Total · per container</p>
                                                    <p className="text-[10px] opacity-75">All-in rate ({bk.surcharges.length} surcharges included)</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-2xl font-black tabular-nums">${fmtUSD(bk.total)}</p>
                                                    <p className="text-[10px] opacity-90">USD / {lane.container}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* AI Insight panel */}
                            {rankedRates.length > 0 && (
                                <div className="bg-gradient-to-br from-indigo-50 via-sky-50 to-cyan-50 dark:from-indigo-500/10 dark:via-sky-500/10 dark:to-cyan-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-2xl p-5" data-testid="ai-insight-panel">
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow">
                                            <Sparkles className="w-4 h-4 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[11px] uppercase tracking-wider font-semibold text-indigo-500 mb-1">AI Insight</p>
                                            <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-line">{insight}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Routing suggestions — hidden per user request */}
                            {false && rankedRates.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Route className="w-4 h-4 text-indigo-500" />
                                        <h3 className="text-sm font-semibold text-slate-700 dark:text-white">Routing options</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="routing-options">
                                        {ROUTING_OPTIONS.map((opt) => (
                                            <div key={opt.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4">
                                                <p className="font-semibold text-slate-800 dark:text-white text-sm">{opt.label}</p>
                                                <p className="text-xs text-slate-500 mt-1">{opt.description}</p>
                                                <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300 mt-3">
                                                    <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {opt.transitDays} days</span>
                                                    <span className={`flex items-center gap-1 ${opt.costDelta < 0 ? 'text-emerald-600 dark:text-emerald-400' : opt.costDelta > 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                                                        <DollarSign className="w-3 h-3" /> {opt.costDelta === 0 ? 'Baseline cost' : `${opt.costDelta > 0 ? '+' : ''}$${opt.costDelta}/container`}
                                                    </span>
                                                </div>
                                                <div className="mt-3 flex flex-wrap gap-1">
                                                    {opt.highlights.map((h, hIdx) => (
                                                        <span key={hIdx} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{h}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Book Now CTA — sits at the end of the page (no longer floating/sticky) */}
                            {rankedRates.length > 0 && (
                                <div className="mt-2 bg-gradient-to-r from-indigo-500 via-sky-500 to-cyan-500 rounded-2xl p-4 flex items-center justify-between gap-4 shadow-lg shadow-indigo-500/20 flex-wrap" data-testid="book-now-bar">
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        {selectedRate && <CarrierLogo carrier={displayCarrier(selectedRate)} domain={selectedRate.carrierDomain} size={40} />}
                                        <div className="text-white min-w-0 flex-1">
                                            <p className="text-[11px] uppercase tracking-wider opacity-90">Ready to book</p>
                                            <p className="font-bold text-lg truncate">
                                                {selectedRate ? `${displayCarrier(selectedRate)} · ${selectedRate.type} · $${fmtUSD(selectedRate.rate)}/container · ${selectedRate.transitDays}d` : 'Select a rate to book'}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => selectedRate && toast.success(`Booking initiated with ${displayCarrier(selectedRate)} (${selectedRate.type}) at $${fmtUSD(selectedRate.rate)}/container`)}
                                        disabled={!selectedRate}
                                        data-testid="book-now-btn"
                                        className="px-5 py-2.5 rounded-xl bg-white text-indigo-600 hover:bg-slate-100 font-bold text-sm flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
                                    >
                                        <Rocket className="w-4 h-4" /> Book Now
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* View All Rates Modal — mixed pool sorted by lowest rate */}
            {showAllRates && (
                <div
                    className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setShowAllRates(false)}
                    data-testid="all-rates-modal"
                >
                    <div
                        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-indigo-50 via-sky-50 to-cyan-50 dark:from-indigo-500/10 dark:via-sky-500/10 dark:to-cyan-500/10 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-slate-800 dark:text-white">All Rates · {lane.origin} → {lane.destination}</h2>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {allRates.length} rates · sorted by lowest total · {lane.container}
                                    {drayageInfo ? ` · trucking $${drayageInfo.totalRate.toFixed(2)} included` : ''}
                                </p>
                            </div>
                            <button
                                onClick={() => setShowAllRates(false)}
                                data-testid="all-rates-close"
                                className="w-9 h-9 rounded-lg hover:bg-slate-200/60 dark:hover:bg-slate-700/60 inline-flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Filters */}
                        {(() => {
                            const maxTransitAvail = Math.max(0, ...allRates.map(r => r.transitDays || 0));
                            const filtered = allRates.filter(r => {
                                if (allRatesFilter.type !== 'All' && r.type !== allRatesFilter.type) return false;
                                if (allRatesFilter.carrier) {
                                    const needle = allRatesFilter.carrier.toLowerCase();
                                    const inName = (r.carrier || '').toLowerCase().includes(needle);
                                    const inScac = (r.scac || '').toLowerCase().includes(needle);
                                    if (!inName && !inScac) return false;
                                }
                                if (allRatesFilter.maxTransit > 0 && (r.transitDays || 0) > allRatesFilter.maxTransit) return false;
                                if (allRatesFilter.minOnTime > 0 && (r.reliability || 0) < allRatesFilter.minOnTime) return false;
                                return true;
                            });
                            return (
                                <>
                                    <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex flex-wrap items-center gap-3" data-testid="all-rates-filters">
                                        {/* Type chips */}
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mr-1">Type</span>
                                            {['All', 'Contract', 'Spot', 'ShipEz'].map(t => (
                                                <button
                                                    key={t}
                                                    onClick={() => setAllRatesFilter(f => ({ ...f, type: t }))}
                                                    data-testid={`filter-type-${t.toLowerCase()}`}
                                                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${allRatesFilter.type === t
                                                        ? 'bg-indigo-600 text-white shadow-sm'
                                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-indigo-400'
                                                        }`}
                                                >
                                                    {t}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Carrier search */}
                                        <div className="flex items-center gap-1.5 ml-2">
                                            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Carrier</span>
                                            <input
                                                type="text"
                                                value={allRatesFilter.carrier}
                                                onChange={(e) => setAllRatesFilter(f => ({ ...f, carrier: e.target.value }))}
                                                placeholder="Search carrier or SCAC…"
                                                data-testid="filter-carrier"
                                                className="px-2.5 py-1 rounded-lg text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 w-44"
                                            />
                                        </div>

                                        {/* Max transit slider */}
                                        {maxTransitAvail > 0 && (
                                            <div className="flex items-center gap-2 ml-2">
                                                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Max transit</span>
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={maxTransitAvail}
                                                    step={1}
                                                    value={allRatesFilter.maxTransit}
                                                    onChange={(e) => setAllRatesFilter(f => ({ ...f, maxTransit: Number(e.target.value) }))}
                                                    data-testid="filter-max-transit"
                                                    className="w-28 accent-indigo-600"
                                                />
                                                <span className="text-xs font-semibold text-slate-700 dark:text-white tabular-nums w-12">
                                                    {allRatesFilter.maxTransit === 0 ? 'Any' : `${allRatesFilter.maxTransit}d`}
                                                </span>
                                            </div>
                                        )}

                                        {/* Min on-time slider */}
                                        <div className="flex items-center gap-2 ml-2">
                                            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Min on-time</span>
                                            <input
                                                type="range"
                                                min={0}
                                                max={100}
                                                step={1}
                                                value={allRatesFilter.minOnTime}
                                                onChange={(e) => setAllRatesFilter(f => ({ ...f, minOnTime: Number(e.target.value) }))}
                                                data-testid="filter-min-ontime"
                                                className="w-28 accent-indigo-600"
                                            />
                                            <span className="text-xs font-semibold text-slate-700 dark:text-white tabular-nums w-12">
                                                {allRatesFilter.minOnTime === 0 ? 'Any' : `${allRatesFilter.minOnTime}%`}
                                            </span>
                                        </div>

                                        {/* Result count + reset */}
                                        <div className="ml-auto flex items-center gap-2">
                                            <span className="text-xs text-slate-500" data-testid="filter-result-count">
                                                {filtered.length} of {allRates.length}
                                            </span>
                                            {(allRatesFilter.type !== 'All' || allRatesFilter.carrier || allRatesFilter.maxTransit > 0 || allRatesFilter.minOnTime > 0) && (
                                                <button
                                                    onClick={() => setAllRatesFilter({ type: 'All', carrier: '', maxTransit: 0, minOnTime: 0 })}
                                                    data-testid="filter-reset"
                                                    className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-300 hover:underline"
                                                >
                                                    Reset
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Table */}
                                    <div className="overflow-y-auto flex-1">
                                        {filtered.length === 0 ? (
                                            <div className="p-12 text-center text-sm text-slate-500" data-testid="filter-empty">
                                                No rates match your filters. <button onClick={() => setAllRatesFilter({ type: 'All', carrier: '', maxTransit: 0, minOnTime: 0 })} className="text-indigo-600 font-semibold hover:underline">Reset filters</button>
                                            </div>
                                        ) : (
                                            <table className="w-full text-sm">
                                                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 text-[11px] uppercase tracking-wider text-slate-500">
                                                    <tr>
                                                        <th className="text-left px-4 py-3 font-semibold">#</th>
                                                        <th className="text-left px-4 py-3 font-semibold">Carrier</th>
                                                        <th className="text-left px-4 py-3 font-semibold">Type</th>
                                                        <th className="text-right px-4 py-3 font-semibold">Total</th>
                                                        <th className="text-right px-4 py-3 font-semibold">Transit</th>
                                                        <th className="text-right px-4 py-3 font-semibold">On-time</th>
                                                        <th className="text-right px-4 py-3 font-semibold">Source</th>
                                                        <th className="text-right px-4 py-3 font-semibold">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                    {filtered.map((r, idx) => {
                                                        const typeClass = {
                                                            Contract: 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',
                                                            Spot: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
                                                            ShipEz: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
                                                        }[r.type] || 'bg-slate-100 text-slate-600';
                                                        const isExpanded = expandedRow === idx;
                                                        const bk = r._breakup;
                                                        return (
                                                            <React.Fragment key={idx}>
                                                                <tr
                                                                    onClick={() => setExpandedRow(isExpanded ? null : idx)}
                                                                    className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 ${idx === 0 ? 'bg-emerald-50/60 dark:bg-emerald-500/5' : ''} ${isExpanded ? 'bg-indigo-50/60 dark:bg-indigo-500/5' : ''}`}
                                                                    data-testid={`all-rate-row-${idx}`}
                                                                >
                                                                    <td className="px-4 py-3 text-slate-400 tabular-nums">
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                                            {idx + 1}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <div className="flex items-center gap-2.5">
                                                                            <CarrierLogo carrier={r.carrier} domain={r.carrierDomain} size={28} />
                                                                            <div className="min-w-0">
                                                                                <p className="font-semibold text-slate-800 dark:text-white truncate">{r.carrier}</p>
                                                                                {r.scac && <p className="text-[10px] text-slate-400 font-mono">{r.scac}</p>}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${typeClass}`}>
                                                                            {r.type}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right font-bold text-slate-800 dark:text-white tabular-nums">
                                                                        ${fmtUSD(r.rate)}
                                                                        {idx === 0 && <span className="ml-1.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">CHEAPEST</span>}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300 tabular-nums">{r.transitDays}d</td>
                                                                    <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300 tabular-nums">{r.reliability}%</td>
                                                                    <td className="px-4 py-3 text-right">
                                                                        <span className="text-[10px] uppercase tracking-wider text-slate-400">
                                                                            {r.source === 'cargorates' ? 'cargorates.ai' : 'rate-matrix'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toast.success(`Booking initiated with ${r.carrier} (${r.type}) at $${fmtUSD(r.rate)}/container`);
                                                                                setShowAllRates(false);
                                                                            }}
                                                                            data-testid={`book-now-row-${idx}`}
                                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
                                                                        >
                                                                            <Rocket className="w-3 h-3" /> Book Now
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                                {isExpanded && bk && (
                                                                    <tr className="bg-slate-50 dark:bg-slate-800/40" data-testid={`all-rate-row-${idx}-breakup`}>
                                                                        <td colSpan={8} className="px-4 py-4">
                                                                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                                                                                <div className="flex items-center justify-between mb-3">
                                                                                    <p className="text-[11px] uppercase tracking-wider font-bold text-indigo-500">Charge breakup · per {lane.container}</p>
                                                                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600">Live</span>
                                                                                </div>
                                                                                <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
                                                                                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-100">
                                                                                        <Ship className="w-4 h-4 text-indigo-500" /> Ocean Freight
                                                                                    </div>
                                                                                    <span className="text-sm font-bold tabular-nums">${fmtUSD(bk.ocean)}</span>
                                                                                </div>
                                                                                {(bk.surcharges || []).length > 0 && (
                                                                                    <div className="py-2">
                                                                                        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2">
                                                                                            Surcharges ({bk.surcharges.length})
                                                                                        </p>
                                                                                        <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                                                                                            {bk.surcharges.map((s, i) => (
                                                                                                <div key={i} className="flex items-center justify-between text-xs py-1">
                                                                                                    <div className="min-w-0 flex-1">
                                                                                                        <span className="font-semibold text-slate-700 dark:text-slate-200">{s.code}</span>
                                                                                                        {s.desc && s.desc !== s.code && (
                                                                                                            <span className="text-slate-400 ml-1.5">· {String(s.desc).slice(0, 50)}{String(s.desc).length > 50 ? '…' : ''}</span>
                                                                                                        )}
                                                                                                    </div>
                                                                                                    <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">${fmtUSD(s.amount)}</span>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                                <div className="mt-2 p-2.5 rounded-lg bg-gradient-to-r from-indigo-500 to-cyan-500 text-white flex items-center justify-between">
                                                                                    <div>
                                                                                        <p className="text-[10px] uppercase tracking-wider opacity-90 font-semibold">Total · per {lane.container}</p>
                                                                                        <p className="text-[10px] opacity-75">All-in rate ({(bk.surcharges || []).length} surcharges)</p>
                                                                                    </div>
                                                                                    <p className="text-xl font-black tabular-nums">${fmtUSD(bk.total)}</p>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                {isExpanded && !bk && (
                                                                    <tr className="bg-slate-50 dark:bg-slate-800/40">
                                                                        <td colSpan={8} className="px-4 py-3 text-center text-xs text-slate-500">
                                                                            Charge breakup not available for this row.
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RateIntelligenceAgent;
